import { firstFromSnapshot, oneFromSnapshot, } from "@buildwithflux/firestore-compatibility-layer";
import { AutoLayoutJob, AutoLayoutState, } from "@buildwithflux/models";
import { guid, silentLogger } from "@buildwithflux/shared";
import { StorageSubscriptionManager } from "../subscriptions";
export class FirestoreAutoLayoutJobRepository {
    db;
    logger;
    subscriptionMgr = new StorageSubscriptionManager();
    firestoreSubscriptions = new Map();
    constructor(db, logger = silentLogger) {
        this.db = db;
        this.logger = logger;
    }
    /* @inheritDoc */
    async setIterationFeedback(userHandle, jobUid, iterationHash, feedback) {
        const rawJob = oneFromSnapshot(await this.db.userJobsCollection(userHandle).doc(jobUid).get(), () => undefined);
        if (!rawJob)
            return false;
        const jobResult = AutoLayoutJob.safeParse(rawJob);
        if (!jobResult.success) {
            this.logger.error(`AutoLayoutJob validation failed in setIterationFeedback for job ${jobUid}`, {
                rawData: JSON.stringify(rawJob),
                validationErrors: jobResult.error.issues,
            });
            throw jobResult.error;
        }
        const job = jobResult.data;
        if (!AutoLayoutState.hasFeedback(job)) {
            return false;
        }
        this.logger.info(`Updating feedback for ${jobUid} iteration ${iterationHash}, feedback: ${feedback}`);
        const feedbackMap = job.feedback;
        feedbackMap[iterationHash] = feedback;
        this.db.userJobsCollection(userHandle).doc(jobUid).set({ feedback: feedbackMap }, { merge: true }).then();
        return true;
    }
    /* @inheritDoc */
    subscribe(userHandle, jobUid, onChange) {
        const jobRef = this.db.userJobsCollection(userHandle).doc(jobUid);
        // Here's the other half - we're erasing the type inside the subscription and then reviving it when we fire the callback.
        const unsubscribe = this.subscriptionMgr.addSubscription(jobUid, onChange, () => {
            const existingSubCount = this.subscriptionMgr.listenerCount(jobUid);
            if (existingSubCount === 0) {
                const firestoreUnsub = this.firestoreSubscriptions.get(jobUid);
                if (firestoreUnsub) {
                    firestoreUnsub();
                    this.firestoreSubscriptions.delete(jobUid);
                }
            }
        });
        const existing = this.firestoreSubscriptions.get(jobUid);
        if (!existing) {
            const firestoreUnsub = jobRef.onSnapshot((snapshot) => {
                const data = snapshot.data();
                if (!data) {
                    this.logger.warn(`Job ${jobUid} does not exist in Firestore!`);
                    return;
                }
                const jobResult = AutoLayoutJob.safeParse(data);
                if (!jobResult.success) {
                    this.logger.error(`AutoLayoutJob validation failed in subscription update for job ${jobUid}`, {
                        rawData: JSON.stringify(data),
                        validationErrors: jobResult.error.issues,
                    });
                    return;
                }
                this.notifyJobChangeListeners(jobUid, { type: "updated", data: jobResult.data });
            });
            this.firestoreSubscriptions.set(jobUid, firestoreUnsub);
        }
        return unsubscribe;
    }
    /* @inheritDoc */
    async save(userHandle, job) {
        this.db.userJobsCollection(userHandle).doc(job.jobUid).set(job, { merge: true }).then();
    }
    /* @inheritDoc */
    async get(userHandle, jobUid) {
        const raw = oneFromSnapshot(await this.db.userJobsCollection(userHandle).doc(jobUid).get(), () => undefined);
        if (!raw)
            return undefined;
        const jobResult = AutoLayoutJob.safeParse(raw);
        if (!jobResult.success) {
            this.logger.error(`AutoLayoutJob validation failed in get job ${jobUid}`, {
                rawData: JSON.stringify(raw),
                validationErrors: jobResult.error.issues,
            });
            throw jobResult.error;
        }
        return jobResult.data;
    }
    /* @inheritDoc */
    async getActiveJobForProject(userHandle, userUid, projectUid) {
        const job = firstFromSnapshot(await this.db
            .userJobsCollection(userHandle)
            .where("submittedByUserUid", "==", userUid)
            .where("type", "==", "autoLayout")
            .where("projectUid", "==", projectUid)
            .where("active", "==", true)
            .orderBy("createAt", "desc")
            .get(), () => undefined);
        if (job?.type !== "autoLayout")
            return undefined;
        return job;
    }
    /* @inheritDoc */
    async saveSelectedIteration(userHandle, jobUid, selectedIterationHash) {
        this.db.userJobsCollection(userHandle).doc(jobUid).set({ selectedIterationHash }, { merge: true }).then();
        return true;
    }
    async saveInternal(userHandle, jobUid, internalMetadata) {
        const updates = {};
        for (const [key, value] of Object.entries(internalMetadata)) {
            if (value !== undefined) {
                updates[`_internal.${key}`] = value;
            }
        }
        if (Object.keys(updates).length > 0) {
            await this.db.userJobsCollection(userHandle).doc(jobUid).update(updates);
        }
    }
    async updateInternal(userHandle, jobUid, fields) {
        const updates = {};
        for (const [key, value] of Object.entries(fields)) {
            if (value !== undefined) {
                updates[`_internal.${key}`] = value;
            }
        }
        if (Object.keys(updates).length > 0) {
            await this.db.userJobsCollection(userHandle).doc(jobUid).update(updates);
        }
    }
    async saveGeneration(userHandle, jobUid, generation) {
        const docId = guid();
        await this.db.userJobGenerationsCollection(userHandle, jobUid).doc(docId).set(generation);
    }
    async saveStateTransition(userHandle, jobUid, record) {
        const docId = guid();
        await this.db.userJobStateHistoryCollection(userHandle, jobUid).doc(docId).set(record);
    }
    notifyJobChangeListeners(jobUid, change) {
        this.subscriptionMgr.notify(jobUid, change);
    }
}
//# sourceMappingURL=FirestoreAutoLayoutJobRepository.js.map